import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ShoppingCart, Plus, Minus, Trash2, CreditCard, Banknote, Search,
  Receipt, History, BarChart2, X, Check, RefreshCw, Loader2,
  Package, PenLine, ChevronRight, TrendingUp, DollarSign, Calendar,
  Printer, Mail, ArrowLeft, AlertTriangle, CheckCircle2, SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { smartSearch } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { value: "cash",     label: "Espèces",   icon: "💵" },
  { value: "card",     label: "Carte",     icon: "💳" },
  { value: "transfer", label: "Virement",  icon: "🏦" },
  { value: "mixed",    label: "Mixte",     icon: "🔀" },
];

// ─── Composant principal ──────────────────────────────────────────────────────
export default function CashRegisterPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pos"); // pos | history | dashboard
  const [parts, setParts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [tvaRate, setTvaRate] = useState(0); // 0 = TTC sans TVA affichée
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [cashReceived, setCashReceived] = useState("");
  const [mixed, setMixed] = useState({ cash: "", card: "" });
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [clientEmail, setClientEmail] = useState("");
  const [historyFilter, setHistoryFilter] = useState("today");
  const [lastReceipt, setLastReceipt] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [tpeStatus, setTpeStatus] = useState(null); // null | "online" | "offline"
  const [tpeProcessing, setTpeProcessing] = useState(false);
  const [tpeEnabled, setTpeEnabled] = useState(true);
  const [tpeResult, setTpeResult] = useState(null); // résultat paiement TPE
  const [tpeTicketOpen, setTpeTicketOpen] = useState(false); // dialog impression ticket
  const [tpePaid, setTpePaid] = useState(false); // TPE déjà payé via bouton direct

  // Article libre
  const [freeItemName, setFreeItemName] = useState("");
  const [freeItemPrice, setFreeItemPrice] = useState("");
  const [freeItemQty, setFreeItemQty] = useState("1");

  useEffect(() => { fetchAll(); }, []);

  const checkTpeStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/tpe/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTpeStatus(res.data.online ? "online" : "offline");
      setTpeEnabled(res.data.online);
    } catch {
      setTpeStatus("offline");
    }
  };

  const handleTpePayment = async (amount, reference) => {
    setTpeProcessing(true);
    setTpeResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API_URL}/api/tpe/payment`,
        { amount, reference: reference || "" },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 130000 }
      );
      const data = res.data;
      setTpeResult(data);
      if (data.tickets && (data.tickets.client || data.tickets.merchant)) {
        setTpeTicketOpen(true);
      }
      if (!data.success) throw new Error("Paiement refusé par le TPE");
      return true;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Paiement TPE refusé";
      throw new Error(msg);
    } finally {
      setTpeProcessing(false);
    }
  };

  const handleTpeCancel = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_URL}/api/tpe/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info("Transaction annulée sur le TPE");
    } catch {}
    setTpeProcessing(false);
  };
  useEffect(() => { if (activeTab === "history") fetchSales(); }, [activeTab, historyFilter]);
  useEffect(() => { if (activeTab === "dashboard") fetchStats(); }, [activeTab]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const [partsRes, settingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/parts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/admin/shop-settings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setParts(partsRes.data || []);
      const tva = settingsRes.data?.tva_rate;
      if (tva !== undefined) setTvaRate(parseFloat(tva) || 0);
    } catch (err) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  const fetchSales = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/sales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { period: historyFilter }
      });
      setSales(res.data || []);
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/sales/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch {}
  };

  // ── Panier ──────────────────────────────────────────────────────────────────
  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.id === item.id);
      if (existing) {
        if (item.quantity !== undefined && existing.qty >= item.quantity) {
          toast.error("Stock insuffisant");
          return prev;
        }
        return prev.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c);
      }
      return [...prev, { ...item, qty: 1 }];
    });
  };

  const updateQty = (id, delta) => {
    setCart(prev => prev.map(c => {
      if (c.id !== id) return c;
      const newQty = c.qty + delta;
      if (newQty <= 0) return null;
      if (c.quantity !== undefined && newQty > c.quantity) {
        toast.error("Stock insuffisant");
        return c;
      }
      return { ...c, qty: newQty };
    }).filter(Boolean));
  };

  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id));
  const clearCart = () => { setCart([]); setClientEmail(""); setCashReceived(""); setMixed({ cash: "", card: "" }); };

  const addFreeItem = () => {
    if (!freeItemName || !freeItemPrice) { toast.error("Nom et prix requis"); return; }
    const price = parseFloat(freeItemPrice);
    if (isNaN(price) || price <= 0) { toast.error("Prix invalide"); return; }
    const item = {
      id: `free_${Date.now()}`,
      name: freeItemName,
      selling_price: price,
      qty: parseInt(freeItemQty) || 1,
      isFree: true,
    };
    setCart(prev => [...prev, item]);
    setFreeItemName(""); setFreeItemPrice(""); setFreeItemQty("1");
    toast.success(`"${freeItemName}" ajouté`);
  };

  // ── Calculs ─────────────────────────────────────────────────────────────────
  const subtotal = cart.reduce((sum, item) => sum + (item.selling_price || item.unit_price || 0) * item.qty, 0);
  const tvaAmount = tvaRate > 0 ? subtotal * (tvaRate / 100) : 0;
  const total = subtotal + tvaAmount;

  const change = paymentMethod === "cash"
    ? (parseFloat(cashReceived) || 0) - total
    : paymentMethod === "mixed"
      ? (parseFloat(mixed.cash) || 0) + (parseFloat(mixed.card) || 0) - total
      : 0;

  // ── Paiement ────────────────────────────────────────────────────────────────
  const handleCheckout = async () => {
    if (cart.length === 0) { toast.error("Panier vide"); return; }
    if (paymentMethod === "cash" && (parseFloat(cashReceived) || 0) < total) {
      toast.error("Montant insuffisant"); return;
    }
    if (paymentMethod === "mixed") {
      const mixedTotal = (parseFloat(mixed.cash) || 0) + (parseFloat(mixed.card) || 0);
      if (mixedTotal < total) { toast.error("Montant insuffisant"); return; }
    }
    setProcessing(true);
    try {
      const token = localStorage.getItem("token");

      // Envoyer au TPE si paiement carte et TPE connecté
      const needsTPE = (paymentMethod === "card" || paymentMethod === "mixed") && tpeEnabled && !tpePaid;
      if (needsTPE) {
        const tpeAmount = paymentMethod === "mixed"
          ? parseFloat(mixed.card) || 0
          : total;
        toast.info(`📡 Envoi de ${tpeAmount.toFixed(2)}€ au TPE...`, { duration: 3000 });
        try {
          await handleTpePayment(tpeAmount);
          toast.success("✅ Paiement TPE accepté !");
        } catch (tpeErr) {
          toast.error(`❌ TPE: ${tpeErr.message}`);
          setProcessing(false);
          return;
        }
      }

      const payload = {
        items: cart.map(item => ({
          part_id: item.isFree ? null : item.id,
          name: item.name,
          quantity: item.qty,
          unit_price: item.selling_price || item.unit_price || 0,
        })),
        subtotal,
        tva_rate: tvaRate,
        tva_amount: tvaAmount,
        total,
        payment_method: paymentMethod,
        cash_received: paymentMethod === "cash" ? parseFloat(cashReceived) || 0 : null,
        cash_change: paymentMethod === "cash" ? Math.max(0, change) : null,
        mixed_cash: paymentMethod === "mixed" ? parseFloat(mixed.cash) || 0 : null,
        mixed_card: paymentMethod === "mixed" ? parseFloat(mixed.card) || 0 : null,
        client_email: clientEmail || null,
      };
      const res = await axios.post(`${API_URL}/api/sales`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastReceipt(res.data);
      setReceiptOpen(true);
      clearCart();
      setTpePaid(false);
      setCheckoutOpen(false);
      toast.success("Vente enregistrée !");
      // Afficher les tickets TPE si disponibles
      if (tpeResult?.tickets && (tpeResult.tickets.client || tpeResult.tickets.merchant)) {
        setTpeTicketOpen(true);
      }
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la vente");
    } finally {
      setProcessing(false);
    }
  };

  // ── Filtres produits ────────────────────────────────────────────────────────
  const filteredParts = parts.filter(p =>
    p.quantity > 0 &&
    (searchQuery === "" || smartSearch(searchQuery, [p.name, p.reference, p.brand, p.category]))
  );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-chivo text-2xl font-bold text-foreground flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-primary" />
              Caisse
            </h1>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-muted-foreground text-sm">Point de vente direct</p>
              {tpeStatus === "online" && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
                  TPE connecté
                </span>
              )}
              {tpeStatus === "offline" && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 font-medium">
                  TPE hors ligne
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            {["pos","history","dashboard"].map(tab => (
              <Button
                key={tab}
                variant={activeTab === tab ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTab(tab)}
                className="text-xs"
              >
                {tab === "pos" && <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />}
                {tab === "history" && <History className="w-3.5 h-3.5 mr-1.5" />}
                {tab === "dashboard" && <BarChart2 className="w-3.5 h-3.5 mr-1.5" />}
                {tab === "pos" ? "Caisse" : tab === "history" ? "Historique" : "Tableau de bord"}
              </Button>
            ))}
          </div>
        </div>

        {/* ── POS ── */}
        {activeTab === "pos" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Colonne gauche : produits */}
            <div className="lg:col-span-2 space-y-4">
              {/* Recherche */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un produit..."
                  className="pl-9 bg-secondary/50"
                />
              </div>

              {/* Grille produits stock */}
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {filteredParts.map(part => (
                    <button
                      key={part.id}
                      onClick={() => addToCart({ ...part, selling_price: part.selling_price || part.unit_price })}
                      className="p-3 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-primary/5 transition-all text-left group"
                    >
                      <div className="flex items-start justify-between mb-1">
                        <Package className="w-4 h-4 text-primary shrink-0" />
                        <span className="text-xs text-muted-foreground">x{part.quantity}</span>
                      </div>
                      <p className="text-sm font-medium text-foreground line-clamp-2 mb-1">{part.name}</p>
                      <p className="text-xs text-muted-foreground mb-2">{part.brand}</p>
                      <p className="font-bold text-primary text-sm">
                        {(part.selling_price || part.unit_price || 0).toFixed(2)}€
                      </p>
                    </button>
                  ))}
                  {filteredParts.length === 0 && (
                    <div className="col-span-3 text-center py-8 text-muted-foreground text-sm">
                      Aucun produit trouvé
                    </div>
                  )}
                </div>
              )}

              {/* Article libre */}
              <Card className="bg-card border-border border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <PenLine className="w-4 h-4 text-primary" />
                    Article libre
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Input
                      value={freeItemName}
                      onChange={e => setFreeItemName(e.target.value)}
                      placeholder="Description..."
                      className="bg-secondary/50 flex-1"
                    />
                    <Input
                      value={freeItemPrice}
                      onChange={e => setFreeItemPrice(e.target.value)}
                      placeholder="Prix €"
                      type="number"
                      className="bg-secondary/50 w-24"
                    />
                    <Input
                      value={freeItemQty}
                      onChange={e => setFreeItemQty(e.target.value)}
                      placeholder="Qté"
                      type="number"
                      className="bg-secondary/50 w-16"
                    />
                    <Button onClick={addFreeItem} size="sm" className="shrink-0">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Colonne droite : panier */}
            <div className="space-y-3">
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-chivo flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-primary" />
                      Panier
                      {cart.length > 0 && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                          {cart.reduce((s, c) => s + c.qty, 0)} art.
                        </span>
                      )}
                    </CardTitle>
                    {cart.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearCart} className="text-xs text-muted-foreground">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Vider
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {cart.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      Panier vide
                    </div>
                  ) : (
                    <>
                      {cart.map(item => (
                        <div key={item.id} className="flex items-center gap-2 py-2 border-b border-border last:border-0">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                            <p className="text-xs text-primary font-semibold">
                              {(item.selling_price || item.unit_price || 0).toFixed(2)}€
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => updateQty(item.id, -1)}
                              className="w-6 h-6 rounded-md bg-secondary flex items-center justify-center hover:bg-secondary/80">
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="w-6 text-center text-sm font-medium">{item.qty}</span>
                            <button onClick={() => updateQty(item.id, 1)}
                              className="w-6 h-6 rounded-md bg-secondary flex items-center justify-center hover:bg-secondary/80">
                              <Plus className="w-3 h-3" />
                            </button>
                            <button onClick={() => removeFromCart(item.id)}
                              className="w-6 h-6 rounded-md text-red-400 hover:bg-red-500/10 flex items-center justify-center ml-1">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="text-sm font-bold text-foreground w-16 text-right shrink-0">
                            {((item.selling_price || item.unit_price || 0) * item.qty).toFixed(2)}€
                          </div>
                        </div>
                      ))}

                      {/* Totaux */}
                      <div className="pt-2 space-y-1">
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Sous-total</span>
                          <span>{subtotal.toFixed(2)}€</span>
                        </div>
                        {tvaRate > 0 && (
                          <div className="flex justify-between text-sm text-muted-foreground">
                            <span>TVA {tvaRate}%</span>
                            <span>{tvaAmount.toFixed(2)}€</span>
                          </div>
                        )}
                        <div className="flex justify-between text-base font-bold text-foreground border-t border-border pt-2">
                          <span>Total</span>
                          <span className="text-primary text-lg">{total.toFixed(2)}€</span>
                        </div>
                      </div>

                      {/* Bouton payer */}
                      <Button
                        className="w-full bg-primary hover:bg-primary/90 mt-2"
                        onClick={() => setCheckoutOpen(true)}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Encaisser {total.toFixed(2)}€
                      </Button>
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700 mt-1 text-sm"
                        onClick={async () => {
                          if (cart.length === 0) { toast.error("Panier vide"); return; }
                          toast.info(`📡 Envoi de ${total.toFixed(2)}€ au TPE...`, { duration: 3000 });
                          setTpeProcessing(true);
                          try {
                            const tpeRes = await axios.post("http://localhost:9999/payment",
                              { amount: total },
                              { timeout: 130000 }
                            );
                            const tpeData = tpeRes.data;
                            setTpeResult(tpeData);
                            setTpePaid(true);
                            toast.success("✅ Paiement TPE accepté ! Confirmez la vente.");
                            setPaymentMethod("card");
                            setCheckoutOpen(true);
                          } catch (err) {
                            const msg = err.response?.data?.detail || err.message || "";
                            if (msg.includes("Network Error") || msg.includes("ECONNREFUSED")) {
                              toast.error("❌ Bridge TPE non démarré — lancez LANCER-TPE-BRIDGE.bat");
                            } else {
                              toast.error(`❌ TPE: ${msg}`);
                            }
                          } finally {
                            setTpeProcessing(false);
                          }
                        }}
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Payer par CB — TPE {total.toFixed(2)}€
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ── HISTORIQUE ── */}
        {activeTab === "history" && (
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "today", label: "Aujourd'hui" },
                { value: "week", label: "Cette semaine" },
                { value: "month", label: "Ce mois" },
                { value: "all", label: "Tout" },
              ].map(f => (
                <Button
                  key={f.value}
                  variant={historyFilter === f.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHistoryFilter(f.value)}
                  className="text-xs"
                >
                  {f.label}
                </Button>
              ))}
            </div>

            {sales.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucune vente sur cette période</p>
              </div>
            ) : (
              <div className="space-y-2">
                {sales.map(sale => (
                  <Card key={sale.id} className="bg-card border-border">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Receipt className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-sm text-foreground">{sale.sale_number}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(sale.created_at).toLocaleDateString("fr-FR", {
                                day: "numeric", month: "short", year: "numeric",
                                hour: "2-digit", minute: "2-digit"
                              })}
                            </p>
                            <div className="flex gap-1 mt-1 flex-wrap">
                              {sale.items?.map((item, i) => (
                                <span key={i} className="text-xs bg-secondary px-2 py-0.5 rounded-full text-muted-foreground">
                                  {item.name} x{item.quantity}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary text-lg">{sale.total.toFixed(2)}€</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            sale.payment_method === "cash" ? "bg-emerald-500/20 text-emerald-400" :
                            sale.payment_method === "card" ? "bg-blue-500/20 text-blue-400" :
                            sale.payment_method === "transfer" ? "bg-purple-500/20 text-purple-400" :
                            "bg-amber-500/20 text-amber-400"
                          }`}>
                            {PAYMENT_METHODS.find(m => m.value === sale.payment_method)?.icon}{" "}
                            {PAYMENT_METHODS.find(m => m.value === sale.payment_method)?.label}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TABLEAU DE BORD ── */}
        {activeTab === "dashboard" && (
          <div className="space-y-4">
            {!stats ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    { label: "Aujourd'hui", value: `${(stats.today_revenue || 0).toFixed(2)}€`, icon: DollarSign, color: "emerald" },
                    { label: "Cette semaine", value: `${(stats.week_revenue || 0).toFixed(2)}€`, icon: TrendingUp, color: "blue" },
                    { label: "Ce mois", value: `${(stats.month_revenue || 0).toFixed(2)}€`, icon: Calendar, color: "purple" },
                    { label: "Nb ventes (mois)", value: stats.month_count || 0, icon: Receipt, color: "amber" },
                  ].map((s, i) => (
                    <Card key={i} className="bg-card border-border">
                      <CardContent className="p-4">
                        <div className={`w-10 h-10 rounded-xl bg-${s.color}-500/10 flex items-center justify-center mb-3`}>
                          <s.icon className={`w-5 h-5 text-${s.color}-400`} />
                        </div>
                        <p className="text-2xl font-bold text-foreground">{s.value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Répartition paiements */}
                <Card className="bg-card border-border">
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Répartition paiements (mois)</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {PAYMENT_METHODS.filter(m => m.value !== "mixed").map(method => {
                      const amount = stats.payment_breakdown?.[method.value] || 0;
                      const pct = stats.month_revenue > 0 ? (amount / stats.month_revenue) * 100 : 0;
                      return (
                        <div key={method.value}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-muted-foreground">{method.icon} {method.label}</span>
                            <span className="font-medium text-foreground">{amount.toFixed(2)}€</span>
                          </div>
                          <div className="h-2 bg-secondary rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary rounded-full transition-all"
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Top produits */}
                {stats.top_products?.length > 0 && (
                  <Card className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-sm font-medium">Top produits vendus (mois)</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {stats.top_products.map((p, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                              {i + 1}
                            </span>
                            <span className="text-sm text-foreground">{p.name}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-primary">{p.total.toFixed(2)}€</p>
                            <p className="text-xs text-muted-foreground">{p.quantity} vendus</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Clôture de caisse */}
                <Card className="bg-card border-amber-500/30 border">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-foreground">Clôture de caisse</p>
                      <p className="text-xs text-muted-foreground">Générer le rapport journalier et réinitialiser</p>
                    </div>
                    <Button
                      variant="outline"
                      className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("token");
                          const res = await axios.post(`${API_URL}/api/sales/close-day`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success("Clôture effectuée !");
                          fetchStats();
                        } catch (e) {
                          toast.error("Erreur clôture");
                        }
                      }}
                    >
                      <Receipt className="w-4 h-4 mr-2" />
                      Clôturer la journée
                    </Button>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Dialog Paiement ── */}
      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Encaissement — {total.toFixed(2)}€
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Mode de paiement */}
            <div className="space-y-2">
              <Label>Mode de paiement</Label>
              <div className="grid grid-cols-2 gap-2">
                {PAYMENT_METHODS.map(m => (
                  <button
                    key={m.value}
                    onClick={() => setPaymentMethod(m.value)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      paymentMethod === m.value
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-card text-muted-foreground hover:border-primary/40"
                    }`}
                  >
                    {m.icon} {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Espèces */}
            {paymentMethod === "cash" && (
              <div className="space-y-2">
                <Label>Montant reçu (€)</Label>
                <Input
                  type="number"
                  value={cashReceived}
                  onChange={e => setCashReceived(e.target.value)}
                  placeholder={total.toFixed(2)}
                  className="bg-secondary/50 text-lg font-bold"
                  autoFocus
                />
                {parseFloat(cashReceived) >= total && (
                  <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                    <p className="text-sm font-bold text-emerald-400 text-center">
                      Rendu : {Math.max(0, change).toFixed(2)}€
                    </p>
                  </div>
                )}
                {/* Raccourcis */}
                <div className="flex gap-2 flex-wrap">
                  {[total, Math.ceil(total / 5) * 5, Math.ceil(total / 10) * 10, Math.ceil(total / 20) * 20, 50, 100].filter((v, i, a) => a.indexOf(v) === i && v >= total).slice(0, 4).map(v => (
                    <Button key={v} variant="outline" size="sm" onClick={() => setCashReceived(v.toString())} className="text-xs">
                      {v.toFixed(0)}€
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Mixte */}
            {paymentMethod === "mixed" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">💵 Espèces (€)</Label>
                  <Input
                    type="number"
                    value={mixed.cash}
                    onChange={e => setMixed(p => ({ ...p, cash: e.target.value }))}
                    placeholder="0.00"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">💳 Carte (€)</Label>
                  <Input
                    type="number"
                    value={mixed.card}
                    onChange={e => setMixed(p => ({ ...p, card: e.target.value }))}
                    placeholder="0.00"
                    className="bg-secondary/50"
                  />
                </div>
                {(parseFloat(mixed.cash) || 0) + (parseFloat(mixed.card) || 0) >= total && (
                  <div className="col-span-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
                    <p className="text-xs font-bold text-emerald-400">
                      Rendu : {Math.max(0, change).toFixed(2)}€
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Email client optionnel */}
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Email client (optionnel — pour le reçu)</Label>
              <Input
                type="email"
                value={clientEmail}
                onChange={e => setClientEmail(e.target.value)}
                placeholder="client@email.com"
                className="bg-secondary/50"
              />
            </div>

            {/* Résumé */}
            <div className="p-3 rounded-xl bg-secondary/50 space-y-1 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>{cart.reduce((s, c) => s + c.qty, 0)} article(s)</span>
                <span>{subtotal.toFixed(2)}€</span>
              </div>
              {tvaRate > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>TVA {tvaRate}%</span><span>{tvaAmount.toFixed(2)}€</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-foreground border-t border-border pt-1">
                <span>Total</span>
                <span className="text-primary">{total.toFixed(2)}€</span>
              </div>
            </div>

            <Button
              className="w-full bg-primary hover:bg-primary/90 h-12 text-base font-bold"
              onClick={handleCheckout}
              disabled={processing || tpeProcessing || cart.length === 0}
            >
              {(processing || tpeProcessing) ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-5 h-5 mr-2" />
              )}
              {tpeProcessing ? "TPE en attente de paiement..." : processing ? "Traitement..." : `Confirmer ${total.toFixed(2)}€`}
            </Button>
            {tpeProcessing && (
              <Button
                variant="outline"
                className="w-full border-red-500/40 text-red-400 hover:bg-red-500/10"
                onClick={handleTpeCancel}
              >
                <X className="w-4 h-4 mr-2" />
                Annuler la transaction TPE
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog Reçu ── */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <CheckCircle2 className="w-5 h-5" />
              Vente enregistrée !
            </DialogTitle>
          </DialogHeader>
          {lastReceipt && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-center">
                <p className="text-3xl font-bold text-emerald-400">{lastReceipt.total?.toFixed(2)}€</p>
                <p className="text-sm text-muted-foreground mt-1">{lastReceipt.sale_number}</p>
              </div>
              {lastReceipt.cash_change > 0 && (
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-center">
                  <p className="text-lg font-bold text-amber-400">
                    Rendu : {lastReceipt.cash_change.toFixed(2)}€
                  </p>
                </div>
              )}
              {tpeResult?.tickets && (tpeResult.tickets.client || tpeResult.tickets.merchant) && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase">Tickets CB</p>
                  <div className="flex gap-2">
                    {tpeResult.tickets.client && (
                      <button onClick={() => { const w=window.open("","_blank"); w.document.write("<html><head><title>Ticket Client</title><style>body{font-family:monospace;font-size:12px;white-space:pre;margin:20px;}</style></head><body>"+tpeResult.tickets.client+"</body></html>"); w.document.close(); w.print(); }} className="flex-1 py-2 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-medium hover:bg-blue-500/20">
                        Ticket Client
                      </button>
                    )}
                    {tpeResult.tickets.merchant && (
                      <button onClick={() => { const w=window.open("","_blank"); w.document.write("<html><head><title>Ticket Commercant</title><style>body{font-family:monospace;font-size:12px;white-space:pre;margin:20px;}</style></head><body>"+tpeResult.tickets.merchant+"</body></html>"); w.document.close(); w.print(); }} className="flex-1 py-2 rounded-xl bg-violet-500/10 border border-violet-500/30 text-violet-400 text-xs font-medium hover:bg-violet-500/20">
                        Ticket Commerçant
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                {lastReceipt.receipt_pdf && (
                  <Button variant="outline" className="flex-1 text-xs" onClick={() => window.open(`${API_URL}${lastReceipt.receipt_pdf}`, "_blank")}>
                    <Printer className="w-3.5 h-3.5 mr-1.5" />Imprimer reçu
                  </Button>
                )}
                <Button className="flex-1 text-xs bg-primary hover:bg-primary/90" onClick={() => { setReceiptOpen(false); setTpeResult(null); }}>
                  <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />Nouvelle vente
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    {/* Dialog impression ticket TPE */}
    {tpeTicketOpen && tpeResult?.tickets && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="bg-card border border-border rounded-2xl p-6 w-full max-w-md shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <span className="text-2xl">✅</span>
            </div>
            <div>
              <h3 className="font-bold text-foreground text-lg">Paiement approuvé</h3>
              <p className="text-sm text-muted-foreground">AE = {tpeResult.ae} — {(tpeResult.amount/100).toFixed(2)} €</p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-4">Souhaitez-vous imprimer le(s) ticket(s) CB ?</p>

          <div className="space-y-2 mb-4">
            {tpeResult.tickets.client && (
              <button
                onClick={() => {
                  const w = window.open('', '_blank');
                  w.document.write('<html><head><title>Ticket Client</title><style>body{font-family:monospace;font-size:12px;white-space:pre;}</style></head><body>' + tpeResult.tickets.client.split('\n').join('<br>') + '</body></html>');
                  w.document.close(); w.print();
                }}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-secondary/30 border border-border hover:border-primary/40 text-sm text-left transition-all"
              >
                <span className="font-medium text-foreground">🧾 Ticket Client</span>
                <span className="text-xs text-primary">Imprimer →</span>
              </button>
            )}
            {tpeResult.tickets.merchant && (
              <button
                onClick={() => {
                  const w = window.open('', '_blank');
                  w.document.write('<html><head><title>Ticket Commerçant</title><style>body{font-family:monospace;font-size:12px;white-space:pre;}</style></head><body>' + tpeResult.tickets.merchant.split('\n').join('<br>') + '</body></html>');
                  w.document.close(); w.print();
                }}
                className="w-full flex items-center justify-between p-3 rounded-xl bg-secondary/30 border border-border hover:border-primary/40 text-sm text-left transition-all"
              >
                <span className="font-medium text-foreground">🧾 Ticket Commerçant</span>
                <span className="text-xs text-primary">Imprimer →</span>
              </button>
            )}
          </div>

          <button
            onClick={() => setTpeTicketOpen(false)}
            className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-all"
          >
            Fermer
          </button>
        </div>
      </div>
    )}

    {/* Dialog TPE en attente */}
    {tpeProcessing && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="bg-card border border-border rounded-2xl p-8 w-full max-w-sm shadow-2xl text-center">
          <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl animate-pulse">💳</span>
          </div>
          <h3 className="font-bold text-foreground text-lg mb-2">En attente du TPE</h3>
          <p className="text-sm text-muted-foreground mb-4">Le client est en train de payer sur le terminal...</p>
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay:'0ms'}} />
            <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay:'150ms'}} />
            <div className="w-2 h-2 rounded-full bg-blue-400 animate-bounce" style={{animationDelay:'300ms'}} />
          </div>
          <button
            onClick={handleTpeCancel}
            className="mt-6 text-xs text-red-400 hover:text-red-300 underline"
          >
            Annuler la transaction
          </button>
        </div>
      </div>
    )}

    </MainLayout>
  );
}
